import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_ETAPA,
  estaEncerrada,
  formatarReais,
  type EtapaFunil,
  type PapelUsuaria,
} from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import { CampoSelecao, CampoTexto, CampoTextoLongo } from './Campo.tsx';
import { Dialogo } from './Dialogo.tsx';

type OportunidadeElegivel = {
  id: string;
  produto: string;
  etapa: EtapaFunil;
  valorMensalCentavos: number;
  contato: { id: string; nome: string; naoContatar: boolean };
  responsavel: { id: string; nome: string };
};

type Usuaria = { id: string; nome: string; papel: PapelUsuaria; ativa: boolean };

/** Hoje no formato do `<input type="date">`, pelo relógio de quem usa. */
function hoje(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/**
 * Início de uma cadência sobre uma Oportunidade.
 *
 * A lista oferece só o que pode receber tentativas: venda em andamento e
 * Contato que aceita ser contatado. A data de início ancora o roteiro inteiro,
 * e a responsável começa sendo quem já conduz a Oportunidade.
 *
 * Escolher outra pessoa é da Administradora. Para os outros papéis o campo só
 * mostra quem conduz: a lista de usuárias é dela, e pedi-la daqui devolvia
 * 403 e um seletor vazio e obrigatório, que impedia a Comercial de iniciar
 * qualquer cadência.
 */
export function IniciarCadencia({ aoFechar }: { aoFechar: () => void }): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const { usuaria } = useSessao();
  const ehAdministradora = usuaria?.papel === 'ADMINISTRADORA';
  const [erro, setErro] = useState<string | null>(null);
  const [oportunidadeId, setOportunidadeId] = useState('');
  const [responsavelId, setResponsavelId] = useState('');

  /**
   * As vendas vêm daqui, e não do Sales: o quadro passou a carregar cada
   * coluna aos poucos (NEX-227), e a lista que ele tem nunca é a inteira. As
   * 500 mais novas cobrem quem inicia cadência — é o lead que acabou de chegar.
   */
  const vendas = useQuery({
    queryKey: ['oportunidades', 'para-cadencia'],
    queryFn: () => chamarApi<OportunidadeElegivel[]>('/oportunidades?limite=500'),
  });
  const oportunidades = vendas.data ?? [];

  const usuarias = useQuery({
    queryKey: ['usuarias'],
    queryFn: () => chamarApi<Usuaria[]>('/usuarias'),
    enabled: ehAdministradora,
  });

  const elegiveis = oportunidades.filter(
    (uma) => !estaEncerrada(uma.etapa) && !uma.contato.naoContatar,
  );
  const candidatas = (usuarias.data ?? []).filter((uma) => uma.ativa);
  const escolhida = elegiveis.find((uma) => uma.id === oportunidadeId);

  const iniciar = useMutation({
    mutationFn: (corpo: { dataInicio: string; responsavelId?: string; observacao: string }) =>
      chamarApi(`/oportunidades/${oportunidadeId}/cadencia`, { metodo: 'POST', corpo }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cadencias'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['oportunidades'] });
      aoFechar();
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível iniciar a cadência.');
    },
  });

  /**
   * Trocar de Oportunidade traz junto quem a conduz: é o destino natural das
   * tentativas, e continua sendo possível escolher outra pessoa depois.
   */
  function aoEscolherOportunidade(id: string): void {
    setOportunidadeId(id);
    setResponsavelId(elegiveis.find((uma) => uma.id === id)?.responsavel.id ?? '');
  }

  function aoEnviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    setErro(null);
    const campos = new FormData(evento.currentTarget);
    iniciar.mutate({
      dataInicio: String(campos.get('dataInicio') ?? ''),
      // Ausente, a API usa quem conduz a Oportunidade.
      ...(ehAdministradora ? { responsavelId } : {}),
      observacao: String(campos.get('observacao') ?? ''),
    });
  }

  return (
    <Dialogo area="Reducee Sales" titulo="Iniciar cadência" aoFechar={aoFechar}>
      <form onSubmit={aoEnviar}>
        <div className="grid gap-3 md:grid-cols-2">
          <CampoSelecao
            rotulo="Lead / oportunidade"
            name="oportunidadeId"
            required
            className="md:col-span-2"
            value={oportunidadeId}
            onChange={(evento) => aoEscolherOportunidade(evento.target.value)}
            dica={
              elegiveis.length === 0
                ? 'Nenhuma Oportunidade em andamento aceita cadência agora.'
                : undefined
            }
          >
            <option value="" disabled>
              Selecione
            </option>
            {elegiveis.map((oportunidade) => (
              <option key={oportunidade.id} value={oportunidade.id}>
                {oportunidade.contato.nome} · {ROTULO_ETAPA[oportunidade.etapa]} ·{' '}
                {formatarReais(oportunidade.valorMensalCentavos)} · {oportunidade.responsavel.nome}
              </option>
            ))}
          </CampoSelecao>

          <CampoTexto
            rotulo="Data de início"
            name="dataInicio"
            type="date"
            required
            defaultValue={hoje()}
          />

          {ehAdministradora ? (
            <CampoSelecao
              rotulo="Responsável"
              name="responsavelId"
              required
              value={responsavelId}
              onChange={(evento) => setResponsavelId(evento.target.value)}
            >
              <option value="" disabled>
                Selecione
              </option>
              {candidatas.map((candidata) => (
                <option key={candidata.id} value={candidata.id}>
                  {candidata.nome}
                </option>
              ))}
            </CampoSelecao>
          ) : (
            <CampoTexto
              rotulo="Responsável"
              name="responsavelDaCadencia"
              value={escolhida?.responsavel.nome ?? usuaria?.nome ?? ''}
              readOnly
              dica="Quem conduz o lead. Direcionar a outra pessoa é da Administradora."
            />
          )}

          <CampoTextoLongo
            rotulo="Observação"
            name="observacao"
            placeholder="Contexto para os contatos"
            className="md:col-span-2"
          />
        </div>

        {erro ? (
          <p role="alert" className="mt-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
            {erro}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-[10px] border border-linha bg-superficie px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={iniciar.isPending || elegiveis.length === 0}
            className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
          >
            Criar cadência
          </button>
        </div>
      </form>
    </Dialogo>
  );
}
